/* @jsx h */
import { defineComponent, ref, h, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMenu } from "@app/vue/providers/index";
import { useCustomer } from "@app/vue/providers/Auth";
import { PartySizeSelector } from "../components/PartySizeSelector";
import type { EventWithShop } from "@lemmonhq/types";
import { fetchEvent } from "@app/server/client";
import { createReservationRequest } from "@app/server/client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { RRule } from "rrule";
import { getTodayInTimezone } from "@lemmonhq/orders";
import { Lightbox, useLocalization } from "@lemmonhq/uikit";

dayjs.extend(utc);
dayjs.extend(timezone);

interface DateSlot {
  date: Date;
  dayName: string;
  dayNumber: number;
  monthName: string;
  timeSlots: string[];
  isToday: boolean;
  isPast: boolean;
}

export const EventDetailsPage = defineComponent({
  name: "EventDetailsPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { shop } = useMenu();
    const { customer, showAuthDialog } = useCustomer();
    const { locale, t } = useLocalization();

    const event = ref<EventWithShop | null>(null);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const showFullImage = ref(false);

    const eventId = computed(() => route.params.eventId as string);
    const shopSlug = computed(() => route.params.shopslug as string);

    // Booking form state
    const partySize = ref(2);
    const selectedDate = ref<Date | null>(null);
    const selectedTime = ref("");
    const name = ref("");
    const email = ref("");
    const phone = ref("");
    const notes = ref("");
    const isSubmitting = ref(false);
    const submitError = ref<string | null>(null);
    const availableDateSlots = ref<DateSlot[]>([]);

    const loadEvent = async () => {
      try {
        loading.value = true;
        error.value = null;
        event.value = await fetchEvent(shopSlug.value, eventId.value);
      } catch (err) {
        console.error("Failed to load event:", err);
        error.value = t("customer.reserve_events.load_event_failed");
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => loadEvent());

    const hasTickets = computed(
      () => event.value?.ticket_types && event.value.ticket_types.length > 0,
    );
    const hasSchedule = computed(
      () =>
        event.value?.schedule !== undefined && event.value?.schedule !== null,
    );
    const isBookingAvailable = computed(
      () => !hasTickets.value && hasSchedule.value,
    );

    const formatDateLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const generateTimeSlots = (schedule: any): string[] => {
      if (schedule.type === "one_off" || schedule.type === "recurring") {
        const startDate = new Date(schedule.start_at);
        const hours = startDate.getHours().toString().padStart(2, "0");
        const minutes = startDate.getMinutes().toString().padStart(2, "0");
        return [`${hours}:${minutes}`];
      }
      return [];
    };

    watch(
      event,
      (evt) => {
        if (!evt?.schedule || !shop.value?.timezone) return;

        const schedule = evt.schedule;
        const tz = shop.value.timezone || "UTC";
        const today = getTodayInTimezone(tz);
        const maxDate = new Date(today);
        maxDate.setDate(maxDate.getDate() + 28);

        let dates: Date[] = [];

        if (schedule.type === "one_off") {
          const eventDate = new Date(schedule.start_at);
          if (eventDate >= today && eventDate <= maxDate) dates.push(eventDate);
        } else if (schedule.type === "recurring") {
          try {
            const rrule = RRule.fromString(schedule.rrule);
            const dtstart = new Date(schedule.start_at);
            const rule = new RRule({ ...rrule.origOptions, dtstart });
            const occurrences = rule.between(today, maxDate, true);
            const excludedDates = new Set(
              (schedule.excluded_dates || []).map((d: string) =>
                formatDateLocal(new Date(d)),
              ),
            );
            dates = occurrences.filter(
              (o) => !excludedDates.has(formatDateLocal(o)),
            );
          } catch (e) {
            console.error("Error parsing RRule:", e);
          }
        }

        const slots: DateSlot[] = dates.map((date) => {
          const todayStr = formatDateLocal(today);
          const dateStr = formatDateLocal(date);
          return {
            date,
            dayName: date.toLocaleDateString(locale.value || "en", {
              weekday: "short",
            }),
            dayNumber: date.getDate(),
            monthName: date.toLocaleDateString(locale.value || "en", {
              month: "short",
            }),
            timeSlots: generateTimeSlots(schedule),
            isToday: dateStr === todayStr,
            isPast: date < today,
          };
        });

        availableDateSlots.value = slots;
        if (slots.length > 0 && !selectedDate.value) {
          selectedDate.value = slots[0].date;
          selectedTime.value = slots[0].timeSlots[0] || "";
        }
      },
      { immediate: true },
    );

    const isFormValid = computed(
      () =>
        partySize.value >= 1 &&
        selectedDate.value &&
        selectedTime.value &&
        name.value.trim() &&
        email.value.trim() &&
        phone.value.trim(),
    );

    const handleDateSelect = (slot: DateSlot) => {
      selectedDate.value = slot.date;
      selectedTime.value = slot.timeSlots[0] || "";
    };

    const isDateSelected = (slot: DateSlot): boolean =>
      selectedDate.value
        ? formatDateLocal(slot.date) === formatDateLocal(selectedDate.value)
        : false;

    const handleSubmit = async () => {
      if (!isFormValid.value || !event.value) return;

      if (!customer.value?.id) {
        showAuthDialog(email.value || undefined);
        return;
      }

      if (!selectedDate.value) {
        submitError.value = t("customer.reserve.validation_date_required");
        return;
      }

      submitError.value = null;
      isSubmitting.value = true;

      try {
        const restaurantTimezone = shop.value?.timezone || "UTC";
        const dateStr = formatDateLocal(selectedDate.value);
        const dateTimeStr = `${dateStr}T${selectedTime.value}:00`;
        const dateTimeUTC = dayjs
          .tz(dateTimeStr, restaurantTimezone)
          .utc()
          .toISOString();

        const reservationRequest = await createReservationRequest({
          shopSlug: shopSlug.value,
          customer_id: customer.value.id,
          requested_time: dateTimeUTC,
          party_size: partySize.value,
          duration_mins: event.value.duration
            ? parseInt(event.value.duration)
            : undefined,
          notes: `Event: ${event.value.title}${notes.value ? `\n\n${notes.value}` : ""}`,
          party_name: name.value,
          party_email: email.value,
          party_phone: phone.value,
        });

        router.push(
          `/restaurant/${shopSlug.value}/reserve/reservation-request/${reservationRequest.id}`,
        );
      } catch (e) {
        console.error("Error submitting reservation:", e);
        submitError.value = t("customer.reserve.submit_failed");
      } finally {
        isSubmitting.value = false;
      }
    };

    return () => (
      <div>
        {loading.value ? (
          <div class="flex items-center justify-center py-16">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
        ) : error.value ? (
          <div class="py-16 text-center">
            <span class="material-symbols-outlined text-4xl text-red-400 mb-3 block">
              error
            </span>
            <p class="text-red-600 text-sm">{error.value}</p>
          </div>
        ) : event.value ? (
          <div class="space-y-8">
            {/* Hero image */}
            {event.value.image && (
              <button
                type="button"
                class="group relative block w-full aspect-[2/1] md:aspect-[3/1] rounded-xl overflow-hidden bg-gray-100 cursor-zoom-in"
                aria-label={t("customer.reserve_events.view_full_image")}
                onClick={() => (showFullImage.value = true)}
              >
                <img
                  src={event.value.image}
                  alt={event.value.title}
                  class="w-full h-full object-cover"
                />
                <span
                  class="material-symbols-outlined absolute bottom-3 right-3 rounded-full p-1.5 text-lg opacity-80 group-hover:opacity-100"
                  style="color: rgba(255,255,255,0.95); background: rgba(0,0,0,0.45);"
                  aria-hidden="true"
                >
                  open_in_full
                </span>
              </button>
            )}
            {showFullImage.value && event.value.image && (
              <Lightbox
                src={event.value.image}
                alt={event.value.title}
                closeLabel={t("customer.reserve_events.close_image")}
                onClose={() => (showFullImage.value = false)}
              />
            )}

            {/* Event header */}
            <div>
              {event.value.tags && event.value.tags.length > 0 && (
                <div class="flex flex-wrap gap-1.5 mb-3">
                  {event.value.tags.map((tag) => (
                    <span
                      key={tag}
                      class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <h1 class="text-2xl md:text-3xl font-light text-gray-900 tracking-tight">
                {event.value.title}
              </h1>
              {event.value.subtitle && (
                <p class="text-base text-gray-500 font-light mt-1">
                  {event.value.subtitle}
                </p>
              )}
              <div class="flex items-center gap-4 mt-3 text-sm text-gray-500">
                {event.value.time && (
                  <div class="flex items-center gap-1.5">
                    <span class="material-symbols-outlined text-base">
                      schedule
                    </span>
                    <span class="font-light">{event.value.time}</span>
                  </div>
                )}
                {event.value.duration && (
                  <div class="flex items-center gap-1.5">
                    <span class="material-symbols-outlined text-base">
                      timelapse
                    </span>
                    <span class="font-light">{event.value.duration}</span>
                  </div>
                )}
                {event.value.location && (
                  <div class="flex items-center gap-1.5">
                    <span class="material-symbols-outlined text-base">
                      location_on
                    </span>
                    <span class="font-light">{event.value.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {(event.value.long_description || event.value.description) && (
              <p class="text-gray-600 font-light leading-relaxed">
                {event.value.long_description || event.value.description}
              </p>
            )}

            {/* Need to know */}
            {event.value.need_to_know &&
              event.value.need_to_know.length > 0 && (
                <div>
                  <h3 class="text-sm font-medium text-gray-900 mb-2">
                    {t("customer.reserve_events.need_to_know")}
                  </h3>
                  <ul class="space-y-1.5">
                    {event.value.need_to_know.map((item, i) => (
                      <li
                        key={i}
                        class="flex items-start gap-2 text-sm text-gray-600 font-light"
                      >
                        <span class="text-gray-400 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Ticketed event notice */}
            {hasTickets.value && (
              <div class="flex items-center gap-3 bg-gray-50 rounded-xl px-5 py-4">
                <span class="material-symbols-outlined text-gray-600 text-xl">
                  confirmation_number
                </span>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {t("customer.reserve_events.ticketed_event")}
                  </p>
                  <p class="text-xs text-gray-500 font-light">
                    {t("customer.reserve_events.ticketed_event_copy")}
                  </p>
                </div>
              </div>
            )}

            {/* Free event notice */}
            {!hasTickets.value && !hasSchedule.value && (
              <div class="flex items-center gap-3 bg-gray-50 rounded-xl px-5 py-4">
                <span class="material-symbols-outlined text-gray-600 text-xl">
                  event_available
                </span>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {t("customer.reserve_events.free_event")}
                  </p>
                  <p class="text-xs text-gray-500 font-light">
                    {t("customer.reserve_events.free_event_copy")}
                  </p>
                </div>
              </div>
            )}

            {/* Booking form */}
            {isBookingAvailable.value && (
              <div class="space-y-6">
                {/* Date selection */}
                {availableDateSlots.value.length > 0 && (
                  <div>
                    <h3 class="text-sm font-medium text-gray-900 mb-3">
                      {t("customer.reserve.select_date")}
                    </h3>
                    <div class="flex flex-wrap gap-2">
                      {availableDateSlots.value.map((slot) => (
                        <button
                          key={formatDateLocal(slot.date)}
                          type="button"
                          onClick={() => handleDateSelect(slot)}
                          disabled={slot.isPast}
                          class={`relative flex flex-col items-center px-4 py-2.5 rounded-xl border transition-all ${
                            slot.isPast
                              ? "opacity-30 cursor-not-allowed"
                              : isDateSelected(slot)
                                ? "bg-gray-900 text-white border-gray-900"
                                : "bg-white border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <span
                            class={`text-xs font-light ${isDateSelected(slot) ? "text-gray-300" : "text-gray-400"}`}
                          >
                            {slot.dayName}
                          </span>
                          <span class="text-lg font-light">
                            {slot.dayNumber}
                          </span>
                          <span
                            class={`text-xs font-light ${isDateSelected(slot) ? "text-gray-300" : "text-gray-400"}`}
                          >
                            {slot.monthName}
                          </span>
                          {slot.isToday && !isDateSelected(slot) && (
                            <div class="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Guests */}
                <PartySizeSelector
                  modelValue={partySize.value}
                  min={1}
                  max={event.value.capacity || 20}
                  onUpdate:modelValue={(val: number) => (partySize.value = val)}
                  compact
                />

                {/* Contact details */}
                <div>
                  <h3 class="text-sm font-medium text-gray-900 mb-3">
                    {t("customer.reserve.your_details")}
                  </h3>
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={name.value}
                      onInput={(e) =>
                        (name.value = (e.target as HTMLInputElement).value)
                      }
                      placeholder={t("customer.reserve.full_name_required")}
                      class="input input-bordered w-full bg-white focus:input-primary transition-all"
                      required
                    />
                    <input
                      type="email"
                      value={email.value}
                      onInput={(e) =>
                        (email.value = (e.target as HTMLInputElement).value)
                      }
                      placeholder={t("customer.reserve.email_required")}
                      class="input input-bordered w-full bg-white focus:input-primary transition-all"
                      required
                    />
                    <input
                      type="tel"
                      value={phone.value}
                      onInput={(e) =>
                        (phone.value = (e.target as HTMLInputElement).value)
                      }
                      placeholder={t("customer.reserve.phone_required")}
                      class="input input-bordered w-full bg-white focus:input-primary transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Notes */}
                <textarea
                  value={notes.value}
                  onInput={(e) =>
                    (notes.value = (e.target as HTMLTextAreaElement).value)
                  }
                  placeholder={t("customer.reserve.special_requests_optional")}
                  class="textarea textarea-bordered w-full bg-white focus:textarea-primary transition-all resize-none text-sm"
                  rows={2}
                />

                {/* Submit */}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!isFormValid.value || isSubmitting.value}
                  class={`btn btn-lg w-full transition-all ${isFormValid.value ? "btn-primary" : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"}`}
                >
                  {isSubmitting.value
                    ? t("customer.reserve.reserving")
                    : t("customer.reserve_events.reserve_for_event")}
                </button>

                {submitError.value && (
                  <div class="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200">
                    {submitError.value}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  },
});
